<script setup>
// @ts-check

import { api } from '@/api'
import BasePaginationLinks from '@/components/base/BasePaginationLinks.vue'
import LoadingSpinner from '@/components/base/LoadingSpinner.vue'
import { usePaginationResponse } from '@/core/composables/usePaginationResponse'
import { ref, toRefs, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import * as _ from 'lodash-es'

const props = defineProps({
  endpoint: {
    type: String,
    required: true
  },
  disableSort: {
    type: Boolean
  }
})

// ⚠️ If you are mutating this model from outside
// you are probably doing it wrong.
const isLoading = defineModel('loading', {
  type: Boolean,
  default: true
})

defineOptions({
  name: 'ServerDatatable'
})

const { t } = useI18n()
const router = useRouter()
const route = useRoute()

const { endpoint: url } = toRefs(props)

/** @type {import('vue').Ref<?PaginationResponse<*>>} */
const response = ref(null)

const hasError = ref(false)

const { items, meta, total } = usePaginationResponse(response)

const updatePage = async (/** @type {number} */ page) => {
  await router.replace({ query: { ...route.query, page } })
}

/**
 * @param sort {?[{ key: string, order: string}]}
 */
const updateSort = async (sort) => {
  if (props.disableSort) {
    return
  }

  const query = _.omit(route.query, ['orderby', 'asc', 'desc'])

  if (!sort?.length) {
    await router.replace({ query })
    return
  }

  const [{ key, order }] = sort

  await router.replace({
    query: {
      ...query,
      orderby: key,
      [order]: true
    }
  })
}

const fetchData = async () => {
  hasError.value = false
  isLoading.value = true
  response.value = null

  try {
    response.value = await api.get(url.value, { params: route.query })
  } catch (error) {
    hasError.value = true
    console.error(error)
  } finally {
    isLoading.value = false
  }
}

watchEffect(fetchData)

defineExpose({
  refresh: fetchData
})
</script>

<template>
  <VDataTableServer
    :loading="isLoading"
    :items
    :items-per-page="route.query.limit"
    :items-length="total"
    :disable-sort
    @update:sort-by="updateSort"
  >
    <template v-for="(_, name) in $slots" #[name]="slotData">
      <slot :name v-bind="slotData || {}" />
    </template>

    <template #no-data>
      <slot name="no-data">
        <div
          class="server-datatable-row flex flex-col items-center gap-3 p-5 py-14"
        >
          <template v-if="hasError">
            <p class="max-w-56">{{ t('general.error_fetching_data') }}</p>
            <PrimaryButton
              :text="t('general.try_again')"
              @click="router.replace({ query: {} })"
            />
          </template>
          <template v-else>
            {{ t('general.no_data') }}
          </template>
        </div>
      </slot>
    </template>

    <template #loading>
      <div
        class="server-datatable-row flex flex-col items-center justify-center gap-y-2 px-5 py-9"
      >
        <LoadingSpinner />
        <p>{{ t('general.loading') }}</p>
      </div>
    </template>

    <template #bottom>
      <BasePaginationLinks
        v-if="meta?.links"
        class="mt-3"
        :links="meta.links"
        @update-page="updatePage"
      />
    </template>
  </VDataTableServer>
</template>

<style lang="scss" scoped>
@reference '../../styles/main.css';

:deep(table) {
  @apply border-separate! table-fixed! w-full! px-0! !-mt-3;

  thead {
    th {
      @apply h-auto! !font-normal;
    }
  }
}

:deep(.v-table__wrapper) {
  @apply overflow-visible!;
}

:deep(.v-data-table-progress) {
  @apply hidden;
}

:deep(.v-data-table-rows-no-data),
:deep(.v-data-table-rows-loading) {
  @apply shadow-md rounded-md;

  td {
    @apply p-0! !border-0;
  }
}

:deep(.server-datatable-row) {
  @apply rounded-md;
  @apply outline-1 -outline-offset-0 outline-secondary-200;
}
</style>
